import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { PoComboOption, PoI18nService, PoNotificationService } from '@po-ui/ng-components';
import { PoStorageService } from '@po-ui/ng-storage';
import { firstValueFrom, map, Observable, of } from 'rxjs';
import { LiteralsWords } from 'src/app/i18n/literals.model';
import { environment } from 'src/environments/environment';
import { SalesBudgetsItems } from '../interfaces/sales-budgets.model';
import { ISalesTaxesResponse, SalesTaxes } from '../interfaces/sales-model';
import { SalesOrdersItems } from '../interfaces/salesOrders.model';
import { LiteralsService } from './literals-service';
import { NetworkService } from './network.service';
import { TypesOfOperationService } from './types-of-operation.service';
import { Functions } from '../functions/functions';

@Injectable({
  providedIn: 'root'
})
export class SalesService {
  literals: LiteralsWords;
  protected networkService: NetworkService;

  constructor(
    private literalsService: LiteralsService,
    private http: HttpClient,
    private poStorageService: PoStorageService,
    private poNotificationService: PoNotificationService,
    private typesOfOperationService: TypesOfOperationService,
    private poI18nService: PoI18nService,
    protected injector: Injector,
    protected functions: Functions
  ) {
    this.literals = this.literalsService.getLiteralModule();
    this.networkService = injector.get(NetworkService);
  }

  isOnline(): boolean {
    return this.networkService.getConnectionStatus().status;
  }

  calculateTotalSales(body: SalesTaxes): Observable<ISalesTaxesResponse> {
    if (this.isOnline()) {
      return this.http.post<ISalesTaxesResponse>(`${environment.url}api/fat/v1/salesTaxes`, body);
    }
    return of();
  }

  getTotalDiscounts(items: SalesOrdersItems[] | SalesBudgetsItems[]): number {
    let discount = 0;
    items.forEach(item => {
      discount += item.discountamount;
    });
    return discount;
  }

  async getDefaultTesParameterStorage(): Promise<string | void> {
    const PARAMETER = 'MV_TESSAI';
    return await this.poStorageService
      .exists(PARAMETER)
      .then(async exist => {
        if (exist) {
          return await this.poStorageService.get(PARAMETER);
        }
        return null;
      })
      .catch(error => {
        this.poNotificationService.error(error);
      });
  }

  getStatusOfItem(item: SalesOrdersItems): string {
    if (item.quantitydelivered === 0) {
      return 'C';
    } else if (item.quantitydelivered > 0 && item.quantitydelivered < item.quantity && item.blockstatus !== 'R') {
      return 'P';
    } else if (item.quantitydelivered >= item.quantity || item.blockstatus === 'R') {
      return 'T';
    }
    return '';
  }

  calculateDiscountOnItems<T>(items: T[]): T[] {
    items.forEach(i => {
      if ((!i['orderid']) && !(i['budgetid'])) {
        if (i['discount'] > 0 && i['pricelist'] === 0) {
          i['amount'] = i['amount'] - i['discountamount'];
          i['unitprice'] = this.functions.getValueField(i['amount'] / i['quantity'], 'unitprice');
        } else if (i['discount'] > 0 && i['pricelist'] > 0) {
          i['amount'] = i['pricelist'] * i['quantity'] - i['discountamount'];
          i['unitprice'] = this.functions.getValueField(i['amount'] / i['quantity'], 'unitprice');
        }
      }
    });
    return items;
  }

  getListPurchaserPresence(): PoComboOption[] {
    return [
      {
        label: this.literals.str0470,
        value: '0'
      },
      {
        label: this.literals.str0471,
        value: '1'
      },
      {
        label: this.literals.str0472,
        value: '2'
      },
      {
        label: this.literals.str0473,
        value: '3'
      },
      {
        label: this.literals.str0474,
        value: '5'
      },
      {
        label: this.literals.str0475,
        value: '9'
      }
    ];
  }

  getReleaseTypeOptions(): PoComboOption[] {
    return [
      {
        value: '1',
        label: `1 - ${this.literals.str0216}`
      },
      {
        value: '2',
        label: `2 - ${this.literals.str0217}`
      }
    ];
  }

  async getDefaultOperationTypeAndSetInTheForm(form: UntypedFormGroup, isUpdate: boolean): Promise<void> {
    if (!isUpdate) {
      await this.typesOfOperationService.getDefaultOperationTypeFromStorage().then(res => {
        form.get('typeofoperation').setValue(res);
      });
    }
  }

  getComboValue(fieldFilter: string): Observable<PoComboOption[]> {
    let field = '';
    const alias = 'sx3';
    const filter = `x3_campo eq '${fieldFilter}'`;
    const language = this.getLanguage();

    if (this.isOnline()) {
      if (language === 'ES') {
        field = 'x3_cboxspa';
      } else if (language === 'EN') {
        field = 'x3_cboxeng';
      } else {
        field = 'x3_cbox';
      }

      const params = new HttpParams()
        .append('alias', alias)
        .append('fields', field)
        .append('filter', filter);

      return this.http.get<string[]>(`${environment.url}api/framework/v1/genericList`, { params })
        .pipe(
          map(f => f['items'][0][field].split(';')),
          map(types => types.map(t => ({
            value: t.split('=')[0],
            label: t.split('=')[1]
          })))
        );
    } else {
      return of([]);
    }
  }

  getLanguage(): string {
    return this.poI18nService.getLanguage().toLocaleUpperCase();
  }

  async getTypeCustomerProspect(person: string): Promise<string> {
    let labelType = '';

    if (this.isOnline()) {
      const types = await firstValueFrom(this.getComboValue('A1_TIPO'));

      types.forEach(res => {
        if (res.value === person) {
          labelType = res.label;
        }
      });
    } else {
      labelType = this.changeType(person)
    }

    return labelType;
  }

  async getTypeFreight(typeFreight: string): Promise<string> {
    let labelType = '';

    if (this.isOnline) {
      const types = await firstValueFrom(this.getComboValue('C5_TPFRETE'));

      types.forEach(res => {
        if (res.value === typeFreight) {
          labelType = res.label;
        }
      });
    }

    return labelType;
  }

  changeType(type: string): string {
    switch (type) {
      case 'F':
        return this.literals.str0158;
      case 'L':
        return this.literals.str0159;
      case 'R':
        return this.literals.str0160;
      case 'S':
        return this.literals.str0161;
      case 'X':
        return this.literals.str0162;
      default:
        return '';
    }
  }
}
